'use client';

import React, { useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/utils/supabase/client';
import { Turnstile } from '@marsidev/react-turnstile';
import styles from '../../styles/register.module.css';
import Navbar from '@components/Navbar';
import Footer from '@components/footer/Footer';

export default function RegisterPageClient() {
  const router = useRouter();
  const supabase = createClient();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState("");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [captchaError, setCaptchaError] = useState('');
  const turnstileRef = useRef(null);

  // Check for error parameters in URL on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      
      if (error) {
        const errorMessages = {
          'oauth_error': 'OAuth authentication failed. Please try again.',
          'exchange_failed': 'Failed to complete authentication. Please try again.',
          'user_error': 'Failed to retrieve user information. Please try again.',
          'user_creation_failed': 'Failed to create user account. Please contact support if this persists.',
          'user_creation_exception': 'An error occurred creating your account. Please try again.'
        };
        
        setErr(errorMessages[error] || 'An authentication error occurred. Please try again.');
        
        // Clear the error from URL after displaying
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log(email);
    setErr(""); // Clear any existing errors
    setSuccess(""); // Clear any success messages
    try {

      if (password !== passwordRepeat) {
        throw "Your passwords don't match! Re-enter your passwords and try again.";
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { captchaToken }
      });

      if (error) throw error;

      // Sign out immediately to prevent race condition with UserContext
      await supabase.auth.signOut();
      console.log(error)
      if (error) throw error.message;

      const input = { id: data.user.id, description, email, firstName, lastName }

      // Insert new user into the database
      const response = await fetch('/api/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw new Error('Failed to create user in database');
      }

      alert('Registration successful. Please check your email to confirm your signup.');
      router.push('/login');
    } catch (error) {
      setSuccess("");
      setErr("Registration failed: " + error);
      console.error('Registration error:', error);
      // Reset CAPTCHA after failed attempt
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setCaptchaToken('');
    }
  };

  const handleGoogleSignIn = async () => {
    setErr(""); // Clear any existing errors
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: 'email profile',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('OAuth error:', error);
        setErr('Failed to sign up with Google. Please try again.');
      }
      // If successful, browser will redirect - no need to handle success
    } catch (err) {
      console.error('OAuth exception:', err);
      setErr('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <Navbar />
      </header>

      {/* Signup Section */}
      <div className={styles.registerContainer}>
        {/* Welcome Section */}
        <div className={styles.welcomeSection}>
          <h1>Join PenguinLearn!</h1>
        </div>

        {/* Signup Content */}
        <div className={styles.registerContent}>
          {/* Signup Form */}
          <div className={styles.registerForm}>
            <form onSubmit={handleSubmit}>
              {/* Google OAuth Button */}
              <div className={styles.registerGroup}>
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className={`${styles.socialBtn} ${styles.googleBtn}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    backgroundColor: '#fff',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#3c4043',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                >
                  <img src="/images/google.png" alt="Google" style={{ width: '20px', height: '20px' }} />
                  Sign up with Google
                </button>
              </div>

              {/* Divider */}
              <div style={{ position: 'relative', margin: '24px 0' }}>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', borderTop: '1px solid #e5e7eb' }} />
                </div>
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
                  <span style={{ padding: '0 8px', backgroundColor: '#fff', color: '#6b7280', fontSize: '14px' }}>
                    Or sign up with email
                  </span>
                </div>
              </div>

              {/* Input Fields */}
              <div className={styles.registerGroup}>
                <input
                  type="text"
                  placeholder="First Name"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete="given-name"
                  className={styles.inputField}
                />
              </div>
              <div className={styles.registerGroup}>
                <input
                  type="text"
                  placeholder="Last Name"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete="family-name"
                  className={styles.inputField}
                />
              </div>
              <div className={styles.registerGroup}>
                <input
                  type="email"
                  placeholder="Email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className={styles.inputField}
                />
              </div>
              <div className={styles.registerGroup}>
                <input
                  type="password"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className={styles.inputField}
                />
              </div>
              <div className={styles.registerGroup}>
                <input
                  type="password"
                  placeholder="Repeat Password"
                  required
                  value={passwordRepeat}
                  onChange={(e) => setPasswordRepeat(e.target.value)}
                  autoComplete="new-password"
                  className={styles.inputField}
                />
              </div>
              <div className={styles.registerGroup}>
                <textarea
                  placeholder="A short description about yourself"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={styles.inputField}
                  rows={3}
                />
              </div>

              {/* CAPTCHA Verification */}
              <div className={styles.registerGroup}>
                {captchaLoading && (
                  <div style={{ height: '65px', backgroundColor: '#f3f4f6', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
                )}
                <Turnstile
                  ref={turnstileRef}
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                  onSuccess={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError('');
                    setCaptchaLoading(false);
                  }}
                  onError={() => {
                    setCaptchaError('CAPTCHA verification failed. Please try again.');
                    setCaptchaLoading(false);
                  }}
                  onExpire={() => {
                    setCaptchaToken('');
                    setCaptchaError('CAPTCHA expired. Please verify again.');
                  }}
                />
                {captchaError && (
                  <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>{captchaError}</p>
                )}
              </div>

              <div className={styles.errorMsg}>
                {err}
              </div>
              <div className={styles.errorMsg}>
                {success}
              </div>
              <button
                type="submit"
                className={styles.registerBtn}
                disabled={!captchaToken || captchaLoading}
              >
                Sign up
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
